import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { createDeliverableChangeOrder, patchDeliverableChangeOrder } from '@/api/Sherpa/ChangeOrder';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { DATE_FORMAT } from '@/constants';
import { useUploadDocuments } from '@/features/ChangeOrder/hooks/useUploadDocuments';
import { useAppDispatch } from '@/store';
import { notifyUser } from '@/store/Toast';
import { getAcceptedFileTypes, sanitizeResponseError } from '@/utils';
import DeliverableHookForm from '../DeliverableForm';
import { type DeliverableChangeOrderSchema, validationSchema } from '../DeliverableForm/validationSchema';

type AddDeliverableModalProps = {
  projectId: string;
  onSubmitted?: () => void;
};

export const AddDeliverableModal = NiceModal.create<AddDeliverableModalProps>(({ onSubmitted, projectId }) => {
  const modal = useModal();
  const dispatch = useAppDispatch();

  const onSuccess = () => {
    onSubmitted?.();
    modal.hide();
    dispatch(
      notifyUser({
        showToast: true,
        toastMessage: 'Deliverable change order created successfully',
        toastType: 'success',
      })
    );
  };

  const { uploadDocuments, isLoading } = useUploadDocuments({
    onUploadReady: ({ documents, params }) =>
      patchDeliverableChangeOrder({
        changeOrderId: params.changeOrderId,
        documents,
      }),
    onSuccess,
    resourceType: 'changeOrderDeliverable',
  });

  const createMutation = useMutation({
    mutationFn: createDeliverableChangeOrder,
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const validateFiles = (templateDocuments: any[]) => {
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    templateDocuments?.forEach((file: File) => {
      const acceptedFileTypes = getAcceptedFileTypes().split(',');
      if (acceptedFileTypes.includes(file.type)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
          toastType: 'failed',
        })
      );

      return null;
    }

    return validFiles;
  };

  const onSubmit = async (formData: DeliverableChangeOrderSchema) => {
    const response = await createMutation.mutateAsync({
      refType: 'project',
      refId: projectId!,
      deliverableId: Number(formData.deliverable?.value),
      reason: formData.reason,
      consequences: formData.noResponseConsequence,
      proposedDue: dayjs(formData.proposedDueDate).format(DATE_FORMAT),
      responseDueBy: dayjs(formData.responseDueBy).format(DATE_FORMAT),
      notes: formData.notes,
    });

    if (formData.templateDocuments?.length) {
      const validFiles = validateFiles(formData.templateDocuments);

      if (validFiles && validFiles.length > 0) {
        uploadDocuments({
          files: formData.templateDocuments,
          params: { changeOrderId: response.id },
        });
      }
    } else {
      onSuccess();
    }
  };

  if (!modal.visible) return null;

  return (
    <Modal customStyle='max-w-xl p-0'>
      <HookFormProvider
        defaultValues={{
          phase: null,
          deliverable: null,
        }}
        resolver={yupResolver(validationSchema) as any}
      >
        <DeliverableHookForm
          title='Add New Deliverable Change Order'
          hide={modal.hide}
          projectId={projectId}
          onSubmit={onSubmit}
          isLoading={createMutation.isLoading || isLoading}
        />
      </HookFormProvider>
    </Modal>
  );
});
